/**
 * Строки раскрытой карточки «Нового года» (вынесены из NyCardView.tsx 04.11): бронь и заявка без времени.
 *   • Бронь (лид со слотом): тап — сетка дня брони + модалка (как было); у брони — плашка предоплаты (правка — в модалке брони,
 *     здесь не дублируем); «Убрать из НГ» — снять «!!!» (POST /api/ny/bookings/:id/unflag, путь модалки), бронь остаётся в сетке.
 *   • Заявка: «Обработан» — штатный путь «Лидов» (как было); «Удалить» — из вкладки в «Скрытые» (DELETE /api/ny/leads/:id);
 *     в «Скрытых» — «Вернуть». Насовсем ничего не стирается.
 */
import { PlayersBadge } from "../../components/shared";
import { LeadDone } from "./DcLeadsSection";
import { fmtDM, fmtStamp, sourceLabel, weekday, NY_LEAD_DONE, type NyBooking, type NyLead } from "./nyModel";
import { bookingPay } from "./nyStatus";
import { errText, nyHideLead, nyRestoreLead, nyUnflag } from "./useNy";
import { NyDel } from "./NyNotes";
import ny from "./dcNy.module.css";

export const hm = (b: NyBooking) => (b.custom_time || b.time || "").slice(0, 5);
/* кнопка внутри строки-кнопки: ни клик, ни Enter/пробел не должны открыть бронь */
const stop = { onClick: (e: React.SyntheticEvent) => e.stopPropagation(), onKeyDown: (e: React.SyntheticEvent) => e.stopPropagation() };

type Act = { onChanged: () => void; onToast: (m: string) => void };

export function BookingRow({ b, name, onOpen, onChanged, onToast }: { b: NyBooking; name: string; onOpen: (b: NyBooking) => void } & Act) {
  const pay = bookingPay(b);
  const unflag = async () => {
    try { await nyUnflag(b.id); onToast("«!!!» снят — бронь убрана из вкладки, в сетке она на месте"); onChanged(); }
    catch (e) { onToast(errText(e, "Не удалось снять «!!!»")); }
  };
  return (
    <div className={`${ny.bk} ${b.type === "lead" ? ny.bkLead : ""}`} role="button" tabIndex={0} title="Открыть бронь в сетке"
      onClick={() => onOpen(b)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(b); } }}>
      <div className={ny.bkRow}>
        <span className={ny.bkWhen}>{fmtDM(b.date)} · {hm(b)}</span>
        <span className={ny.bkWd}>{weekday(b.date)}</span>
        <span className={ny.bkQuest}>{b.quest || "—"}</span>
        {/* канон 05.09: число игроков — только PlayersBadge; клик — карточка клиента по брони. Обёртка гасит всплытие:
            карточка клиента рисуется внутри строки, и тап по её фону (закрыть) иначе открыл бы бронь */}
        <span {...stop}>
          <PlayersBadge count={b.players} bookingId={b.id} fallbackName={name} size="sm" />
        </span>
        {b.type === "lead" ? <span className={ny.bkTag}>лид</span> : null}
      </div>
      {b.comment ? <div className={ny.bkNote}>{b.comment}</div> : null}
      <div className={ny.bkFoot}>
        {pay ? <span className={`${ny.pay} ${pay.paid ? ny.payOk : ny.payNo}`} title="Предоплата правится в модалке брони">{pay.text}</span> : null}
        <span className={ny.bkAct} {...stop}>
          <NyDel onDelete={unflag} label="Убрать из НГ: снять «!!!», бронь останется в сетке" idle="Убрать из НГ" armed="Убрать?" />
        </span>
      </div>
    </div>
  );
}

export function LeadRow({ l, onChanged, onToast }: { l: NyLead } & Act) {
  const open = l.status === "new";
  const hide = async () => {
    try { await nyHideLead(l.id); onToast("Заявка удалена из вкладки — вернуть можно в «Скрытых»"); onChanged(); }
    catch (e) { onToast(errText(e, "Не удалось удалить заявку")); }
  };
  const restore = async () => {
    try { await nyRestoreLead(l.id); onToast("Заявка возвращена во вкладку"); onChanged(); }
    catch (e) { onToast(errText(e, "Не удалось вернуть заявку")); }
  };
  return (
    <div className={`${ny.ld} ${open && !l.hidden ? "" : ny.ldDone}`}>
      <div className={ny.ldHead}>
        <span className={ny.src} title={l.source ?? undefined}>{sourceLabel(l)}</span>
        <span>{fmtStamp(l.created_at)}</span>
        <span className={ny.ldState}>{l.hidden ? "удалена из вкладки" : open ? "новая" : l.status === "dismissed" ? "обработана" : l.status}</span>
      </div>
      {l.when || l.people ? (
        <div className={ny.ldWhen}>
          {l.when ? <span>Когда: <b>{l.when}</b></span> : null}
          {l.people ? <PlayersBadge count={l.people} size="sm" /> : null}
        </div>
      ) : null}
      {l.wishes ? <div className={ny.ldText}>{l.wishes}</div> : null}
      {l.manual && l.by ? <div className={ny.ldBy}>Внёс: {l.by}</div> : null}
      <div className={ny.ldActs}>
        {l.hidden ? (
          <button type="button" className={ny.btn} onClick={() => { void restore(); }}>Вернуть</button>
        ) : (
          <>
            {open ? <LeadDone id={l.id} onChanged={onChanged} onToast={onToast} texts={NY_LEAD_DONE} /> : null}
            <NyDel onDelete={hide} label="Удалить заявку из вкладки (вернуть — в «Скрытых»)" idle="Удалить" />
          </>
        )}
      </div>
    </div>
  );
}
